import * as mat3 from './mat3';
import * as mat4 from './mat4';
import type { Box3, Mat3, Mat4, OBB3, Quat, Vec3 } from './types';
import * as vec3 from './vec3';

export function create(): OBB3 {
    return { center: [0, 0, 0], halfExtents: [1, 1, 1], rotation: mat3.create() };
}

export function clone(a: OBB3): OBB3 {
    return {
        center: [a.center[0], a.center[1], a.center[2]],
        halfExtents: [a.halfExtents[0], a.halfExtents[1], a.halfExtents[2]],
        rotation: mat3.clone(a.rotation),
    };
}

export function copy(out: OBB3, a: OBB3): OBB3 {
    out.center[0] = a.center[0];
    out.center[1] = a.center[1];
    out.center[2] = a.center[2];
    out.halfExtents[0] = a.halfExtents[0];
    out.halfExtents[1] = a.halfExtents[1];
    out.halfExtents[2] = a.halfExtents[2];
    out.rotation[0] = a.rotation[0];
    out.rotation[1] = a.rotation[1];
    out.rotation[2] = a.rotation[2];
    out.rotation[3] = a.rotation[3];
    out.rotation[4] = a.rotation[4];
    out.rotation[5] = a.rotation[5];
    out.rotation[6] = a.rotation[6];
    out.rotation[7] = a.rotation[7];
    out.rotation[8] = a.rotation[8];
    return out;
}

/**
 * Sets an OBB from center, half extents, and a rotation matrix.
 * @param out the OBB to store the result
 * @param center the center of the OBB
 * @param halfExtents the half extents of the OBB
 * @param rotation the Mat3 rotation matrix
 * @returns the OBB with the given center, half extents, and rotation
 */
export function set(out: OBB3, center: Vec3, halfExtents: Vec3, rotation: Mat3): OBB3 {
    out.center[0] = center[0];
    out.center[1] = center[1];
    out.center[2] = center[2];
    out.halfExtents[0] = halfExtents[0];
    out.halfExtents[1] = halfExtents[1];
    out.halfExtents[2] = halfExtents[2];
    out.rotation[0] = rotation[0];
    out.rotation[1] = rotation[1];
    out.rotation[2] = rotation[2];
    out.rotation[3] = rotation[3];
    out.rotation[4] = rotation[4];
    out.rotation[5] = rotation[5];
    out.rotation[6] = rotation[6];
    out.rotation[7] = rotation[7];
    out.rotation[8] = rotation[8];
    return out;
}

/**
 * Sets an OBB from center, half extents, and a quaternion.
 * Convenience helper for users who store orientation as a quaternion.
 *
 * @param out - The OBB to store the result
 * @param center - The center of the OBB
 * @param halfExtents - The half extents of the OBB
 * @param q - The quaternion representing the OBB's orientation
 * @returns out
 */
export function setFromCenterHalfExtentsQuaternion(out: OBB3, center: Vec3, halfExtents: Vec3, q: Quat): OBB3 {
    out.center[0] = center[0];
    out.center[1] = center[1];
    out.center[2] = center[2];
    out.halfExtents[0] = halfExtents[0];
    out.halfExtents[1] = halfExtents[1];
    out.halfExtents[2] = halfExtents[2];
    mat3.fromQuat(out.rotation, q);
    return out;
}

/**
 * Creates an OBB from an axis-aligned bounding box (AABB).
 * The resulting OBB will have the same center and extents as the AABB,
 * with no rotation (identity orientation).
 *
 * @param out - The OBB to store the result
 * @param aabb - The AABB (min and max corners)
 * @returns out
 */
export function setFromBox3(out: OBB3, aabb: Box3): OBB3 {
    // Center = (min + max) / 2
    out.center[0] = (aabb[0] + aabb[3]) * 0.5;
    out.center[1] = (aabb[1] + aabb[4]) * 0.5;
    out.center[2] = (aabb[2] + aabb[5]) * 0.5;

    // Half extents = (max - min) / 2
    out.halfExtents[0] = (aabb[3] - aabb[0]) * 0.5;
    out.halfExtents[1] = (aabb[4] - aabb[1]) * 0.5;
    out.halfExtents[2] = (aabb[5] - aabb[2]) * 0.5;

    // Identity rotation
    mat3.identity(out.rotation);

    return out;
}

/**
 * Tests whether a point is contained within an OBB.
 *
 * @param obb - The OBB to test
 * @param point - The point to test
 * @returns true if the point is inside the OBB
 */
export function containsPoint(obb: OBB3, point: Vec3): boolean {
    // Vector from center to point
    const dx = point[0] - obb.center[0];
    const dy = point[1] - obb.center[1];
    const dz = point[2] - obb.center[2];

    // Project onto each OBB axis (columns of the rotation matrix) and compare against
    // the half extents. The projections are used once, so keep them as scalars.
    const r = obb.rotation;
    const lx = dx * r[0] + dy * r[1] + dz * r[2];
    const ly = dx * r[3] + dy * r[4] + dz * r[5];
    const lz = dx * r[6] + dy * r[7] + dz * r[8];

    return (
        Math.abs(lx) <= obb.halfExtents[0] &&
        Math.abs(ly) <= obb.halfExtents[1] &&
        Math.abs(lz) <= obb.halfExtents[2]
    );
}

/**
 * Clamps a point to the surface or interior of an OBB.
 * Reference: Closest Point on OBB to Point in Real-Time Collision Detection
 * by Christer Ericson (chapter 5.1.4)
 *
 * @param out - The clamped point result
 * @param obb - The OBB
 * @param point - The point to clamp
 * @returns out
 */
export function clampPoint(out: Vec3, obb: OBB3, point: Vec3): Vec3 {
    // OBB axes are the columns of the rotation matrix, read directly from r[].
    const r = obb.rotation;

    // Vector from center to point (captured before writing out, in case out === point)
    const dx = point[0] - obb.center[0];
    const dy = point[1] - obb.center[1];
    const dz = point[2] - obb.center[2];

    // Start at center, then walk along each axis by the clamped projection distance.
    out[0] = obb.center[0];
    out[1] = obb.center[1];
    out[2] = obb.center[2];

    // x axis = r[0..2]
    let dist = dx * r[0] + dy * r[1] + dz * r[2];
    dist = Math.max(-obb.halfExtents[0], Math.min(obb.halfExtents[0], dist));
    out[0] += r[0] * dist;
    out[1] += r[1] * dist;
    out[2] += r[2] * dist;

    // y axis = r[3..5]
    dist = dx * r[3] + dy * r[4] + dz * r[5];
    dist = Math.max(-obb.halfExtents[1], Math.min(obb.halfExtents[1], dist));
    out[0] += r[3] * dist;
    out[1] += r[4] * dist;
    out[2] += r[5] * dist;

    // z axis = r[6..8]
    dist = dx * r[6] + dy * r[7] + dz * r[8];
    dist = Math.max(-obb.halfExtents[2], Math.min(obb.halfExtents[2], dist));
    out[0] += r[6] * dist;
    out[1] += r[7] * dist;
    out[2] += r[8] * dist;

    return out;
}

/**
 * Tests whether an OBB intersects with another OBB using the Separating Axis Theorem.
 * Reference: OBB-OBB Intersection in Real-Time Collision Detection
 * by Christer Ericson (chapter 4.4.1)
 *
 * @param a - The first OBB
 * @param b - The second OBB
 * @param epsilon - Small value to prevent arithmetic errors when edges are parallel
 * @returns true if the OBBs intersect
 */
const _intersectsOBB3_R: number[][] = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
];
const _intersectsOBB3_tInA = /*@__PURE__*/ vec3.create();
const _intersectsOBB3_AbsR: number[][] = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
];

export function intersectsOBB3(a: OBB3, b: OBB3, epsilon = Number.EPSILON): boolean {
    const rotA = a.rotation;
    const rotB = b.rotation;

    // R[i][j] = dot(A axis i, B axis j); the OBB axes are the columns of the rotation
    // matrices (axis i of A = rotA[3i..3i+2]), computed inline without scratch vectors.
    for (let i = 0; i < 3; i++) {
        const ai = i * 3;
        const ax = rotA[ai];
        const ay = rotA[ai + 1];
        const az = rotA[ai + 2];
        for (let j = 0; j < 3; j++) {
            const bj = j * 3;
            _intersectsOBB3_R[i][j] = ax * rotB[bj] + ay * rotB[bj + 1] + az * rotB[bj + 2];
        }
    }

    // Translation (b.center - a.center) brought into a's frame: tInA[i] = dot(t, A axis i)
    const tx = b.center[0] - a.center[0];
    const ty = b.center[1] - a.center[1];
    const tz = b.center[2] - a.center[2];
    _intersectsOBB3_tInA[0] = tx * rotA[0] + ty * rotA[1] + tz * rotA[2];
    _intersectsOBB3_tInA[1] = tx * rotA[3] + ty * rotA[4] + tz * rotA[5];
    _intersectsOBB3_tInA[2] = tx * rotA[6] + ty * rotA[7] + tz * rotA[8];

    // Compute common subexpressions with epsilon
    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            _intersectsOBB3_AbsR[i][j] = Math.abs(_intersectsOBB3_R[i][j]) + epsilon;
        }
    }

    let ra: number, rb: number;

    // Test axes L = A0, L = A1, L = A2
    for (let i = 0; i < 3; i++) {
        ra = a.halfExtents[i];
        rb =
            b.halfExtents[0] * _intersectsOBB3_AbsR[i][0] +
            b.halfExtents[1] * _intersectsOBB3_AbsR[i][1] +
            b.halfExtents[2] * _intersectsOBB3_AbsR[i][2];
        if (Math.abs(_intersectsOBB3_tInA[i]) > ra + rb) return false;
    }

    // Test axes L = B0, L = B1, L = B2
    for (let i = 0; i < 3; i++) {
        ra =
            a.halfExtents[0] * _intersectsOBB3_AbsR[0][i] +
            a.halfExtents[1] * _intersectsOBB3_AbsR[1][i] +
            a.halfExtents[2] * _intersectsOBB3_AbsR[2][i];
        rb = b.halfExtents[i];
        if (
            Math.abs(
                _intersectsOBB3_tInA[0] * _intersectsOBB3_R[0][i] +
                    _intersectsOBB3_tInA[1] * _intersectsOBB3_R[1][i] +
                    _intersectsOBB3_tInA[2] * _intersectsOBB3_R[2][i],
            ) >
            ra + rb
        )
            return false;
    }

    // Test axis L = A0 x B0
    ra = a.halfExtents[1] * _intersectsOBB3_AbsR[2][0] + a.halfExtents[2] * _intersectsOBB3_AbsR[1][0];
    rb = b.halfExtents[1] * _intersectsOBB3_AbsR[0][2] + b.halfExtents[2] * _intersectsOBB3_AbsR[0][1];
    if (Math.abs(_intersectsOBB3_tInA[2] * _intersectsOBB3_R[1][0] - _intersectsOBB3_tInA[1] * _intersectsOBB3_R[2][0]) > ra + rb)
        return false;

    // Test axis L = A0 x B1
    ra = a.halfExtents[1] * _intersectsOBB3_AbsR[2][1] + a.halfExtents[2] * _intersectsOBB3_AbsR[1][1];
    rb = b.halfExtents[0] * _intersectsOBB3_AbsR[0][2] + b.halfExtents[2] * _intersectsOBB3_AbsR[0][0];
    if (Math.abs(_intersectsOBB3_tInA[2] * _intersectsOBB3_R[1][1] - _intersectsOBB3_tInA[1] * _intersectsOBB3_R[2][1]) > ra + rb)
        return false;

    // Test axis L = A0 x B2
    ra = a.halfExtents[1] * _intersectsOBB3_AbsR[2][2] + a.halfExtents[2] * _intersectsOBB3_AbsR[1][2];
    rb = b.halfExtents[0] * _intersectsOBB3_AbsR[0][1] + b.halfExtents[1] * _intersectsOBB3_AbsR[0][0];
    if (Math.abs(_intersectsOBB3_tInA[2] * _intersectsOBB3_R[1][2] - _intersectsOBB3_tInA[1] * _intersectsOBB3_R[2][2]) > ra + rb)
        return false;

    // Test axis L = A1 x B0
    ra = a.halfExtents[0] * _intersectsOBB3_AbsR[2][0] + a.halfExtents[2] * _intersectsOBB3_AbsR[0][0];
    rb = b.halfExtents[1] * _intersectsOBB3_AbsR[1][2] + b.halfExtents[2] * _intersectsOBB3_AbsR[1][1];
    if (Math.abs(_intersectsOBB3_tInA[0] * _intersectsOBB3_R[2][0] - _intersectsOBB3_tInA[2] * _intersectsOBB3_R[0][0]) > ra + rb)
        return false;

    // Test axis L = A1 x B1
    ra = a.halfExtents[0] * _intersectsOBB3_AbsR[2][1] + a.halfExtents[2] * _intersectsOBB3_AbsR[0][1];
    rb = b.halfExtents[0] * _intersectsOBB3_AbsR[1][2] + b.halfExtents[2] * _intersectsOBB3_AbsR[1][0];
    if (Math.abs(_intersectsOBB3_tInA[0] * _intersectsOBB3_R[2][1] - _intersectsOBB3_tInA[2] * _intersectsOBB3_R[0][1]) > ra + rb)
        return false;

    // Test axis L = A1 x B2
    ra = a.halfExtents[0] * _intersectsOBB3_AbsR[2][2] + a.halfExtents[2] * _intersectsOBB3_AbsR[0][2];
    rb = b.halfExtents[0] * _intersectsOBB3_AbsR[1][1] + b.halfExtents[1] * _intersectsOBB3_AbsR[1][0];
    if (Math.abs(_intersectsOBB3_tInA[0] * _intersectsOBB3_R[2][2] - _intersectsOBB3_tInA[2] * _intersectsOBB3_R[0][2]) > ra + rb)
        return false;

    // Test axis L = A2 x B0
    ra = a.halfExtents[0] * _intersectsOBB3_AbsR[1][0] + a.halfExtents[1] * _intersectsOBB3_AbsR[0][0];
    rb = b.halfExtents[1] * _intersectsOBB3_AbsR[2][2] + b.halfExtents[2] * _intersectsOBB3_AbsR[2][1];
    if (Math.abs(_intersectsOBB3_tInA[1] * _intersectsOBB3_R[0][0] - _intersectsOBB3_tInA[0] * _intersectsOBB3_R[1][0]) > ra + rb)
        return false;

    // Test axis L = A2 x B1
    ra = a.halfExtents[0] * _intersectsOBB3_AbsR[1][1] + a.halfExtents[1] * _intersectsOBB3_AbsR[0][1];
    rb = b.halfExtents[0] * _intersectsOBB3_AbsR[2][2] + b.halfExtents[2] * _intersectsOBB3_AbsR[2][0];
    if (Math.abs(_intersectsOBB3_tInA[1] * _intersectsOBB3_R[0][1] - _intersectsOBB3_tInA[0] * _intersectsOBB3_R[1][1]) > ra + rb)
        return false;

    // Test axis L = A2 x B2
    ra = a.halfExtents[0] * _intersectsOBB3_AbsR[1][2] + a.halfExtents[1] * _intersectsOBB3_AbsR[0][2];
    rb = b.halfExtents[0] * _intersectsOBB3_AbsR[2][1] + b.halfExtents[1] * _intersectsOBB3_AbsR[2][0];
    if (Math.abs(_intersectsOBB3_tInA[1] * _intersectsOBB3_R[0][2] - _intersectsOBB3_tInA[0] * _intersectsOBB3_R[1][2]) > ra + rb)
        return false;

    // No separating axis found - OBBs must be intersecting
    return true;
}

const _intersectsBox3_obbFromAABB = /*@__PURE__*/ create();

/**
 * Tests whether an OBB intersects with an AABB.
 *
 * @param obb - The OBB
 * @param aabb - The AABB (axis-aligned bounding box)
 * @returns true if they intersect
 */
export function intersectsBox3(obb: OBB3, aabb: Box3): boolean {
    const obbFromAABB = setFromBox3(_intersectsBox3_obbFromAABB, aabb);
    return intersectsOBB3(obb, obbFromAABB);
}

/**
 * Applies a 4x4 transformation matrix to an OBB.
 * This can be used to transform the bounding volume with the world matrix
 * of a 3D object to keep both entities in sync.
 *
 * @param out - The transformed OBB
 * @param obb - The OBB to transform
 * @param matrix - The 4x4 transformation matrix
 * @returns out
 */
const _applyMatrix4_rotationMat = /*@__PURE__*/ mat3.create();

export function applyMatrix4(out: OBB3, obb: OBB3, matrix: Mat4): OBB3 {
    const e = matrix;

    // Extract scale from matrix
    let sx = Math.sqrt(e[0] * e[0] + e[1] * e[1] + e[2] * e[2]);
    const sy = Math.sqrt(e[4] * e[4] + e[5] * e[5] + e[6] * e[6]);
    const sz = Math.sqrt(e[8] * e[8] + e[9] * e[9] + e[10] * e[10]);

    // Handle negative scale (reflection)
    const det = mat4.determinant(matrix);
    if (det < 0) sx = -sx;

    // Extract rotation
    mat3.fromMat4(_applyMatrix4_rotationMat, matrix);

    // Remove scale from rotation
    const invSX = 1 / sx;
    const invSY = 1 / sy;
    const invSZ = 1 / sz;

    _applyMatrix4_rotationMat[0] *= invSX;
    _applyMatrix4_rotationMat[1] *= invSX;
    _applyMatrix4_rotationMat[2] *= invSX;
    _applyMatrix4_rotationMat[3] *= invSY;
    _applyMatrix4_rotationMat[4] *= invSY;
    _applyMatrix4_rotationMat[5] *= invSY;
    _applyMatrix4_rotationMat[6] *= invSZ;
    _applyMatrix4_rotationMat[7] *= invSZ;
    _applyMatrix4_rotationMat[8] *= invSZ;

    // Combine rotations: out.rotation = extractedRotation * obb.rotation
    mat3.multiply(out.rotation, _applyMatrix4_rotationMat, obb.rotation);

    // Scale half extents
    out.halfExtents[0] = obb.halfExtents[0] * Math.abs(sx);
    out.halfExtents[1] = obb.halfExtents[1] * Math.abs(sy);
    out.halfExtents[2] = obb.halfExtents[2] * Math.abs(sz);

    // Transform center through the full matrix (rotation + translation)
    vec3.transformMat4(out.center, obb.center, matrix);

    return out;
}
